import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, type ColDef, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useGridCopyMenu } from './gridCommon';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import StockChart from './StockChart';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

// 종목별 ETF 편입 갯수 집계
interface StockRow {
  d: string;
  prodPdf: string;
  prodNm: string;
  etfCnt: number;
  c?: number | null;
  o?: number | null;
  h?: number | null;
  l?: number | null;
  v?: number | null;
  rt?: number | null;
}

// 선택 종목을 담은 ETF 목록
interface EtfRow {
  d: string;
  etfProdId: string;
  etfProdNm: string;
  prodPdf: string;
  prodNm: string;
  etfCnt: number;
  etfC?: number | null;   // ETF 종가
  etfV?: number | null;   // ETF 거래량
  etfRt?: number | null;  // ETF 등락률
  wei?: number | null;
}

const numFmt = (v: any) => v == null ? '' : Number(v).toLocaleString();
const pctFmt = (v: any) => v == null ? '' : `${Number(v).toFixed(2)}`;
const signColor = (v: any) => {
  const n = Number(v);
  return n > 0 ? '#dc2626' : n < 0 ? '#1d4ed8' : '#374151';
};

const EtfPdf: React.FC = () => {
  const copyMenu1 = useGridCopyMenu();
  const copyMenu2 = useGridCopyMenu();
  const [date, setDate] = useState('');
  const [stockRows, setStockRows] = useState<StockRow[]>([]);
  const [etfRows, setEtfRows] = useState<EtfRow[]>([]);
  const [selectedStock, setSelectedStock] = useState<StockRow | null>(null);
  const [selectedEtf, setSelectedEtf] = useState<EtfRow | null>(null);
  const [chartProd, setChartProd] = useState<{ id: string; nm: string } | null>(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [loadingEtf, setLoadingEtf] = useState(false);

  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  // 상단: 종목별 ETF 편입 갯수
  const fetchStock = useCallback((d?: string) => {
    setLoadingStock(true);
    const params: Record<string, any> = {};
    const useDate = d ?? date;
    if (useDate) params.d = useDate;
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/etf/pdfstock',
      params,
      onSuccess: (data) => {
        const r = (data as StockRow[]) || [];
        setStockRows(r);
        if (!useDate && r.length > 0) setDate(r[0].d);
        setLoadingStock(false);
      },
      onError: () => { setStockRows([]); setLoadingStock(false); },
    });
  }, [axiosInstance, m, logger, date]);

  // 중단: 선택 종목 ETF 목록
  const fetchEtf = useCallback((row: StockRow) => {
    setSelectedStock(row);
    setChartProd({ id: row.prodPdf, nm: row.prodNm });
    setEtfRows([]);
    setLoadingEtf(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/etf/pdf',
      params: { prodPdf: row.prodPdf, d: row.d },
      onSuccess: (data) => { setEtfRows((data as EtfRow[]) || []); setLoadingEtf(false); },
      onError: () => { setEtfRows([]); setLoadingEtf(false); },
    });
  }, [axiosInstance, m, logger]);

  useEffect(() => { fetchStock(); }, []);

  // 상단 그리드: 종목별 집계
  const stockCols = useMemo<ColDef<StockRow>[]>(() => [
    { field: 'prodPdf', headerName: '종목코드', width: 95, cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'prodNm', headerName: '종목명', width: 120, filter: 'agTextColumnFilter' },
    { field: 'etfCnt', headerName: 'ETF수', width: 75, type: 'numericColumn', cellStyle: { fontWeight: 600 } },
    { field: 'c', headerName: '종가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { fontWeight: 600 } },
    { field: 'o', headerName: '시가', width: 85, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
    { field: 'h', headerName: '고가', width: 85, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#dc2626' } },
    { field: 'l', headerName: '저가', width: 85, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { color: '#1d4ed8' } },
    { field: 'rt', headerName: '등락률%', width: 90, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'v', headerName: '거래량', width: 110, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
  ], []);

  // 중단 그리드: ETF 목록 (EtfAnalysis pdfCols 동일 스타일)
  const etfCols = useMemo<ColDef<EtfRow>[]>(() => [
    { field: 'etfProdId', headerName: 'ETF코드', width: 90, cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'etfProdNm', headerName: 'ETF명', flex: 1, minWidth: 200, filter: 'agTextColumnFilter' },
    { field: 'wei', headerName: '비중%', width: 80, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: { textAlign: 'right', fontWeight: 600 } },
    { field: 'etfC', headerName: 'ETF종가', width: 90, type: 'numericColumn', valueFormatter: p => numFmt(p.value), cellStyle: { fontWeight: 600 } },
    { field: 'etfRt', headerName: 'ETF등락%', width: 95, type: 'numericColumn', valueFormatter: p => pctFmt(p.value), cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }) },
    { field: 'etfV', headerName: 'ETF거래량', flex: 1, minWidth: 110, type: 'numericColumn', valueFormatter: p => numFmt(p.value) },
  ], []);

  const labelStyle: React.CSSProperties = { fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' };
  const btnStyle: React.CSSProperties = { padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 };
  const titleStyle: React.CSSProperties = { margin: 0, fontSize: '12px', color: '#1e40af', fontWeight: 700 };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 헤더 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={labelStyle}>조회일자:</label>
          <DateInput value={date} onChange={setDate} style={{ border: '2px solid #3b82f6' }} />
          <button onClick={() => fetchStock()} style={btnStyle}>{loadingStock ? '조회중...' : '조회'}</button>
          {stockRows.length > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#1e40af', fontWeight: 500 }}>
              종목 {stockRows.length.toLocaleString()}개
            </span>
          )}
        </div>
      </div>

      {/* 상단 그리드: 종목별 ETF 편입 갯수 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
        <h3 style={{ ...titleStyle, marginBottom: '8px' }}>종목별 ETF 편입 갯수</h3>
        <div className="ag-theme-quartz" style={{ height: 300, width: '100%', fontSize: '11px' }}>
          <AgGridReact {...copyMenu1.gridProps}
            rowData={stockRows}
            columnDefs={stockCols}
            defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
            rowSelection="single"
            onRowClicked={e => { if (e.data) fetchEtf(e.data); }}
            getRowId={p => p.data.prodPdf}
          />
        </div>
      </div>

      {/* 중단 그리드: 선택 종목의 ETF 목록 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={titleStyle}>
            ETF 수익률
            {selectedStock && (
              <span style={{ marginLeft: '10px', fontWeight: 500, color: '#0f172a' }}>
                — {selectedStock.prodNm}
                <span style={{ color: '#64748b', fontFamily: 'monospace', marginLeft: '6px' }}>{selectedStock.prodPdf}</span>
                <span style={{ color: '#64748b', marginLeft: '8px' }}>({selectedStock.etfCnt}개 ETF)</span>
              </span>
            )}
          </h3>
          {loadingEtf && <span style={{ fontSize: '12px', color: '#1e40af' }}>로딩중...</span>}
        </div>
        <div className="ag-theme-quartz" style={{ height: 250, width: '100%', fontSize: '11px' }}>
          <AgGridReact {...copyMenu2.gridProps}
            rowData={etfRows}
            columnDefs={etfCols}
            defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
            rowSelection="single"
            onRowClicked={e => { if (e.data) setSelectedEtf(e.data); }}
            getRowId={p => p.data.etfProdId}
          />
        </div>
      </div>

      {/* ETF 차트 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
        <h3 style={{ ...titleStyle, marginBottom: '8px' }}>
          ETF 차트
          {selectedEtf && (
            <span style={{ marginLeft: '10px', fontWeight: 500, color: '#0f172a' }}>
              — {selectedEtf.etfProdNm}
              <span style={{ color: '#64748b', fontFamily: 'monospace', marginLeft: '6px' }}>{selectedEtf.etfProdId}</span>
            </span>
          )}
        </h3>
        {selectedEtf && <StockChart prodId={selectedEtf.etfProdId} prodNm={selectedEtf.etfProdNm} height={350} />}
      </div>

      {/* 종목 차트 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px' }}>
        <h3 style={{ ...titleStyle, marginBottom: '8px' }}>
          종목 차트
          {chartProd && (
            <span style={{ marginLeft: '10px', fontWeight: 500, color: '#0f172a' }}>
              — {chartProd.nm}
              <span style={{ color: '#64748b', fontFamily: 'monospace', marginLeft: '6px' }}>{chartProd.id}</span>
            </span>
          )}
        </h3>
        {chartProd && <StockChart prodId={chartProd.id} prodNm={chartProd.nm} height={350} />}
      </div>

      {copyMenu1.menu}
      {copyMenu2.menu}
    </div>
  );
};

export default EtfPdf;
